import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw, XCircle } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { errDetail, settle } from "./settlementApi";

// Retry (same key + same details) or cancel a failed / stuck settlement.
export function FailureActions({ bookingId, groupId, settlementKey, body, detail, onDone }) {
  const [busy, setBusy] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);

  const retry = async () => {
    setBusy("retry"); setErr(null);
    try {
      const payload = { ...body, confirm: true, idempotency_key: settlementKey };
      await (groupId ? settle.groupCommit(groupId, payload) : settle.commit(bookingId, payload));
      toast.success("Settlement saved");
      onDone?.();
    } catch (e) { setErr(errDetail(e)); } finally { setBusy(""); }
  };
  const cancel = async () => {
    if (!reason.trim()) return toast.error("Add a reason for cancelling");
    setBusy("cancel"); setErr(null);
    try {
      const r = await settle.cancel(settlementKey, reason.trim());
      toast.success(r.payments_kept?.length ? "Settlement cancelled — card payments taken stay recorded" : "Settlement cancelled");
      onDone?.();
    } catch (e) { setErr(errDetail(e)); } finally { setBusy(""); }
  };
  const d = err || detail || {};
  return (
    <div className="border border-red-200 bg-red-50/60 p-4 space-y-3" data-testid="settlement-failure-box">
      <div className="flex gap-2 text-[12px] text-red-800">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-medium" data-testid="settlement-failure-message">{d.message || "This settlement did not finish."}</div>
          {d.rolled_back?.length > 0 && <div className="mt-1 text-red-700/80">Reversed: {d.rolled_back.join(", ")}</div>}
          {d.payments_kept?.length > 0 && <div className="mt-1 text-red-700/80" data-testid="settlement-payments-kept">Card payments kept: {d.payments_kept.length} (reused on retry)</div>}
        </div>
      </div>
      {body && (
        <Button onClick={retry} disabled={!!busy} size="sm" className="bg-[#282828] hover:bg-black text-white text-[11px]" data-testid="settlement-retry-btn">
          {busy === "retry" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />} Retry settlement
        </Button>
      )}
      <div className="flex gap-2">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancelling" className="h-8 text-[12px] bg-white" data-testid="settlement-cancel-reason" />
        <Button onClick={cancel} disabled={!!busy} size="sm" variant="outline" className="border-red-300 text-red-700 text-[11px] shrink-0" data-testid="settlement-cancel-btn">
          {busy === "cancel" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />} Cancel settlement
        </Button>
      </div>
    </div>
  );
}
